import {mapObject, noop} from '@benchling/js-utils';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ContainerSummary, StorableSummaryExtras} from 'constants/SampleConstants';
import {checkSamplesForStatusWarnings} from 'inventory/sample-status/SampleStatusWarning';
import SampleStatusWarningBannerStack from 'inventory/sample-status/SampleStatusWarningBannerStack';
import {BaseSampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import {TYPE} from 'samples/transfers/input-sample/InputSampleConstants';
import renderPossibleInputContainerContentsError from 'samples/transfers/InputContainerContentsError';
import {
  ContainerSummaryWithContainedItemIds,
  TransferPaneSampleStatusState,
  UpdateTransferModalStateHandler,
  UpdateTransferPaneSampleStatusStateHandler,
} from 'samples/transfers/TransferModalUtil';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';

import ContainerViewContainer from '../containers/ContainerViewContainer';
import StorableSearchInput from '../storables/StorableSearchInput';

export interface ChooseInputContainerProps {
  container: ContainerSummaryWithContainedItemIds | null;
  errors: Array<string>;
  registryId?: string;
  inputSampleStatusState: TransferPaneSampleStatusState;
  sampleStatusWarningTrackingParams: BaseSampleStatusWarningTrackingParams;
  onUpdateSampleStatusState: UpdateTransferPaneSampleStatusStateHandler;
  onUpdateModalState: UpdateTransferModalStateHandler;
}

interface State {
  containedItemIdsByContainerId: Record<string, Array<string>>;
}

class ChooseInputContainer extends React.Component<ChooseInputContainerProps, State> {
  state: State = {
    containedItemIdsByContainerId: {},
  };

  handleSearchSuccess = ({storableSummaryExtras}: {storableSummaryExtras: StorableSummaryExtras}): void => {
    const containedItemsByContainerId = storableSummaryExtras.containedItemsByContainerId;
    if (containedItemsByContainerId) {
      const containedItemIdsByContainerId = mapObject(containedItemsByContainerId, (containedItems) =>
        containedItems.map(({id}) => id)
      );
      this.setState((oldState) => ({
        ...oldState,
        containedItemIdsByContainerId: {
          ...oldState.containedItemIdsByContainerId,
          ...containedItemIdsByContainerId,
        },
      }));
    }
  };

  handleContainerChange = (containers: Array<ContainerSummary>): void => {
    const inputContainer = containers.length
      ? {
          ...containers[0],
          containedItemIds: this.state.containedItemIdsByContainerId[containers[0].id] ?? [],
        }
      : null;
    this.props.onUpdateModalState({inputContainer});
    this.updateInputSampleStatusWarnings(inputContainer);
  };

  updateInputSampleStatusWarnings = (container: ContainerSummary | null): void => {
    if (container == null) {
      this.props.onUpdateSampleStatusState(
        {shouldShowStatusWarnings: false, canProceedOnSampleStatus: false, selectedSampleIds: []},
        'inputSampleStatusState'
      );
      return;
    }
    const {shouldShowWarning, samples} = checkSamplesForStatusWarnings({samples: [container]});
    this.props.onUpdateSampleStatusState(
      {
        shouldShowStatusWarnings: shouldShowWarning,
        canProceedOnSampleStatus: false,
        warningInputOutputType: TYPE.EXISTING_CONTAINER,
        samples: samples ?? null,
        selectedSampleIds: samples?.map(({id}) => id) ?? [],
      },
      'inputSampleStatusState'
    );
  };

  setCanProceedOnSampleStatus = (canProceed: boolean): void => {
    this.props.onUpdateSampleStatusState({canProceedOnSampleStatus: canProceed}, 'inputSampleStatusState');
  };

  renderPossibleWarning(container: ContainerSummary): JSX.Element | null {
    return this.props.inputSampleStatusState.shouldShowStatusWarnings &&
      this.props.inputSampleStatusState.samples != null ? (
      <div className="u-small-spacing-top">
        <SampleStatusWarningBannerStack
          shouldProceed={this.props.inputSampleStatusState.canProceedOnSampleStatus}
          canDeselectSamples={false}
          onChangeShouldProceed={this.setCanProceedOnSampleStatus}
          samples={this.props.inputSampleStatusState.samples}
          setSelectedSampleIds={noop}
          selectedSampleIds={this.props.inputSampleStatusState.selectedSampleIds}
          trackingParamsWithSampleIds={{
            ...this.props.sampleStatusWarningTrackingParams,
            sampleIds: [container.id],
          }}
        />
      </div>
    ) : null;
  }

  renderErrorBanners(): JSX.Element | null {
    return (
      <div className="u-small-spacing-top">
        <InlineBannerStack
          messages={this.props.errors.map((message) => ({text: message, bannerType: InlineBannerType.ERROR}))}
        />
      </div>
    );
  }

  render(): JSX.Element {
    return (
      <div>
        <StorableSearchInput
          onChange={this.handleContainerChange}
          registryId={this.props.registryId}
          selectedStorables={this.props.container ? [this.props.container] : []}
          storableTypes={[ModelTypeConstants.CONTAINER]}
          onSearchSuccess={this.handleSearchSuccess}
        />
        {(() => {
          if (this.props.container) {
            return (
              <>
                {this.renderErrorBanners()}
                {renderPossibleInputContainerContentsError({inputContainer: this.props.container})}
                {this.renderPossibleWarning(this.props.container)}
                <ContainerViewContainer
                  containerId={this.props.container.id}
                  isWritableOverride={false}
                  showFullView={false}
                />
              </>
            );
          }
          return null;
        })()}
      </div>
    );
  }
}

export default ChooseInputContainer;
